import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabaseClient';

const PAGE_SIZE = 30;

/* ── Helpers ──────────────────────────────────────── */
function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

function getPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return pages;
}

function formatDatetime(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  const tz = 'America/Monterrey';
  const d = date.toLocaleDateString('es-MX', {
    timeZone: tz,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const t = date.toLocaleTimeString('es-MX', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  return `${d}, ${t}`;
}

const ACTION_LABELS = {
  INSERT: 'Creación',
  CREATE: 'Creación',
  UPDATE: 'Edición',
  DELETE: 'Eliminación',
  ASSIGN: 'Asignación',
  UNASSIGN: 'Desasignación',
};

const ACTION_CLASSES = {
  INSERT: 'log-action--insert',
  CREATE: 'log-action--create',
  UPDATE: 'log-action--update',
  DELETE: 'log-action--delete',
  ASSIGN: 'log-action--assign',
  UNASSIGN: 'log-action--unassign',
};

/** Pretty-print a JSONB field as human-readable key-value pairs */
function formatJsonField(data) {
  if (!data || typeof data !== 'object') return null;
  const FIELD_LABELS = {
    id: 'ID',
    user_id: 'ID Usuario',
    requirement_id: 'ID Requerimiento',
    activity_id: 'ID Actividad',
    log_date: 'Fecha',
    total_hours: 'Horas Totales',
    extended_hours: 'Horas Extendidas',
    comment: 'Comentario',
    created_at: 'Creado',
    updated_at: 'Actualizado',
  };
  return Object.entries(data).map(([key, val]) => ({
    label: FIELD_LABELS[key] || key,
    value: val == null ? '—' : String(val),
    key,
  }));
}

/* ── Small icons (16×16) ─────────────────────────── */
function IconBack() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconChevronLeft() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function IconChevronRight() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function IconFilter() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function IconClear() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function IconExpand() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}

function IconCollapse() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}

/* ── Detail Modal for time_logs_audit ─────────────── */
function DetailModal({ row, userMap, onClose }) {
  const oldFields = formatJsonField(row.old_data);
  const newFields = formatJsonField(row.new_data);
  const userName = userMap[row.changed_by_user_id] || '—';

  return (
    <>
      <div className="log-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="log-detail-modal" role="dialog" aria-modal="true">
        <div className="log-detail-header">
          <h3 className="log-detail-title">
            Detalle de Auditoría — Registro #{row.time_log_id}
          </h3>
          <button className="log-detail-close" onClick={onClose} aria-label="Cerrar">
            <IconClear />
          </button>
        </div>

        <div className="log-detail-meta">
          <div className="log-detail-meta-item">
            <span className="log-detail-meta-label">Acción:</span>
            <span className={`log-action-badge ${ACTION_CLASSES[row.action] || ''}`}>
              {ACTION_LABELS[row.action] || row.action}
            </span>
          </div>
          <div className="log-detail-meta-item">
            <span className="log-detail-meta-label">Usuario:</span>
            <span className="log-detail-meta-value">{userName}</span>
          </div>
          <div className="log-detail-meta-item">
            <span className="log-detail-meta-label">Fecha:</span>
            <span className="log-detail-meta-value">{formatDatetime(row.changed_at)}</span>
          </div>
        </div>

        <div className="log-detail-data-grid">
          {/* Old Data */}
          <div className="log-detail-data-section">
            <h4 className="log-detail-data-title log-detail-data-title--old">Datos Anteriores</h4>
            {oldFields ? (
              <div className="log-detail-data-table">
                {oldFields.map((f) => (
                  <div key={f.key} className="log-detail-data-row">
                    <span className="log-detail-data-key">{f.label}</span>
                    <span className="log-detail-data-val">{f.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="log-detail-data-empty">Sin datos anteriores</p>
            )}
          </div>

          {/* New Data */}
          <div className="log-detail-data-section">
            <h4 className="log-detail-data-title log-detail-data-title--new">Datos Nuevos</h4>
            {newFields ? (
              <div className="log-detail-data-table">
                {newFields.map((f) => {
                  const changed =
                    oldFields &&
                    oldFields.find((o) => o.key === f.key)?.value !== f.value;
                  return (
                    <div
                      key={f.key}
                      className={`log-detail-data-row${changed ? ' log-detail-data-row--changed' : ''}`}
                    >
                      <span className="log-detail-data-key">{f.label}</span>
                      <span className="log-detail-data-val">{f.value}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="log-detail-data-empty">Sin datos nuevos</p>
            )}
          </div>
        </div>

        <div className="log-detail-footer">
          <button className="log-btn-close" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </>
  );
}

/* ── Component ────────────────────────────────────── */
function LogsViewer({ config, onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userMap, setUserMap] = useState({});
  const [detailRow, setDetailRow] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);

    let query = supabase
      .from(config.table)
      .select(config.select)
      .order(config.orderBy, { ascending: config.orderAscending ?? false });

    if (dateFrom) {
      query = query.gte('changed_at', `${dateFrom}T00:00:00`);
    }
    if (dateTo) {
      query = query.lte('changed_at', `${dateTo}T23:59:59`);
    }
    if (actionFilter && config.actionFilterOptions) {
      query = query.eq('action', actionFilter);
    }

    const { data } = await query;
    const fetched = data || [];

    /* Build user lookup for time_logs_audit (no FK joins) */
    if (config.needsUserLookup && fetched.length > 0) {
      const ids = [...new Set(fetched.map((r) => r.changed_by_user_id).filter(Boolean))];
      if (ids.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, full_name')
          .in('id', ids);
        const map = {};
        (users || []).forEach((u) => { map[u.id] = u.full_name; });
        setUserMap(map);
      }
    }

    setRows(fetched);
    setLoading(false);
  }, [config, dateFrom, dateTo, actionFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [search, dateFrom, dateTo, actionFilter]);

  /* ── Filtering ── */
  const filteredRows = rows.filter((row) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (config.searchKeys || []).some((key) => {
      let val;
      if (key === '_user_name') {
        val = userMap[row.changed_by_user_id] || '';
      } else if (key === '_dev_names') {
        const prev = (row.previous_dev_ids || []).map((d) => d.full_name || '').join(' ');
        const next = (row.new_dev_ids || []).map((d) => d.full_name || '').join(' ');
        val = `${prev} ${next}`;
      } else {
        val = getNestedValue(row, key);
      }
      return String(val || '').toLowerCase().includes(s);
    });
  });

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const pageList = getPageList(safePage, totalPages);

  const hasActiveFilters = search || dateFrom || dateTo || actionFilter;

  function clearFilters() {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setActionFilter('');
  }

  function toggleExpandRow(id) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function countLabel() {
    if (hasActiveFilters && filteredRows.length !== rows.length) {
      return `Se encontraron ${filteredRows.length} de ${rows.length} registros`;
    }
    return `Se encontraron ${rows.length} registro${rows.length !== 1 ? 's' : ''}`;
  }

  /* ── Cell rendering ── */
  function renderCell(col, row) {
    if (col.type === 'status_badge') {
      const status = getNestedValue(row, col.key);
      if (!status) return <span className="log-status-badge log-status-badge--empty">—</span>;
      return (
        <span
          className="log-status-badge"
          style={{
            background: status.color ? `${status.color}20` : 'rgba(34,34,34,0.08)',
            color: status.color || '#222222',
            borderLeft: `3px solid ${status.color || '#ccc'}`,
          }}
        >
          {status.name}
        </span>
      );
    }

    if (col.type === 'status_field_badge') {
      const val = row[col.key];
      const isClient = val === 'client';
      return (
        <span
          className="log-status-field-badge"
          style={{
            background: isClient ? 'rgba(46, 0, 85, 0.08)' : 'rgba(0, 17, 93, 0.08)',
            color: isClient ? '#2E0055' : '#00115D',
            borderLeft: `3px solid ${isClient ? '#2E0055' : '#00115D'}`,
          }}
        >
          {isClient ? 'Estatus Cliente' : 'Estatus Interno'}
        </span>
      );
    }

    if (col.type === 'action_badge') {
      const val = row[col.key];
      return (
        <span className={`log-action-badge ${ACTION_CLASSES[val] || ''}`}>
          {ACTION_LABELS[val] || val}
        </span>
      );
    }

    if (col.type === 'datetime') {
      return formatDatetime(getNestedValue(row, col.key));
    }

    if (col.type === 'user_lookup') {
      return userMap[row[col.key]] || '—';
    }

    if (col.type === 'comment_text') {
      const val = row[col.key];
      if (!val) return <span className="log-comment-empty">—</span>;
      return <span className="log-comment-text">{val}</span>;
    }

    if (col.type === 'dev_snapshot') {
      const arr = row[col.key];
      if (!Array.isArray(arr) || arr.length === 0) return <span className="log-dev-empty">—</span>;
      return (
        <div className="log-dev-pills">
          {arr.map((d, i) => (
            <span key={d.user_id || i} className="log-dev-pill">{d.full_name || '—'}</span>
          ))}
        </div>
      );
    }

    const val = getNestedValue(row, col.key);
    return val ?? '—';
  }

  return (
    <div className="log-viewer">
      {/* ── Header ── */}
      <div className="log-viewer-header">
        <button className="log-back-btn" onClick={onBack}>
          <IconBack />
          <span>Regresar</span>
        </button>
        <h1 className="log-viewer-title">{config.label}</h1>
      </div>

      {/* ── Read-only notice ── */}
      <div className="log-readonly-banner">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
        Solo lectura — estos registros son inmutables y no pueden ser modificados.
      </div>

      {/* ── Filters ── */}
      <div className="log-filters">
        <div className="log-filters-row">
          <div className="log-search-wrap">
            <IconSearch />
            <input
              type="text"
              className="log-search"
              placeholder="Buscar en registros…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {config.actionFilterOptions && (
            <div className="log-filter-group">
              <IconFilter />
              <select
                className="log-filter-select"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
              >
                {config.actionFilterOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="log-filters-row">
          <div className="log-date-filters">
            <div className="log-date-group">
              <label className="log-date-label">Desde:</label>
              <input
                type="date"
                className="log-date-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="log-date-group">
              <label className="log-date-label">Hasta:</label>
              <input
                type="date"
                className="log-date-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>

          <div className="log-filter-actions">
            {hasActiveFilters && (
              <button className="log-clear-btn" onClick={clearFilters}>
                <IconClear />
                <span>Limpiar filtros</span>
              </button>
            )}
            {!loading && (
              <span className="log-count">{countLabel()}</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="log-loading">Cargando registros…</div>
      ) : filteredRows.length === 0 ? (
        <div className="log-empty">
          {hasActiveFilters
            ? 'No se encontraron resultados con los filtros aplicados.'
            : 'No hay registros disponibles.'}
        </div>
      ) : (
        <>
          <div className="log-table-wrap">
            <table className="log-table">
              <thead>
                <tr>
                  {config.hasDetailView && <th className="log-th-expand" />}
                  {config.columns.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row) => {
                  const isExpanded = config.hasDetailView && expandedRows.has(row.id);
                  const colSpan = config.columns.length + (config.hasDetailView ? 1 : 0);
                  const oldFields = isExpanded ? formatJsonField(row.old_data) : null;
                  const newFields = isExpanded ? formatJsonField(row.new_data) : null;
                  return (
                    <React.Fragment key={row.id}>
                      <tr>
                        {config.hasDetailView && (
                          <td className="log-td-expand">
                            <button
                              className="log-expand-btn"
                              onClick={() => toggleExpandRow(row.id)}
                              title={isExpanded ? 'Colapsar' : 'Ver detalle'}
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? <IconCollapse /> : <IconExpand />}
                            </button>
                          </td>
                        )}
                        {config.columns.map((col) => (
                          <td key={col.key}>{renderCell(col, row)}</td>
                        ))}
                      </tr>
                      {isExpanded && (
                        <tr className="log-detail-row">
                          <td colSpan={colSpan} className="log-detail-row-td">
                            <div className="log-inline-detail">
                              <div className="log-inline-detail-header">
                                Detalle del Registro #{row.time_log_id}
                                <button
                                  className="log-detail-expand-full"
                                  onClick={() => setDetailRow(row)}
                                  title="Ver en ventana completa"
                                >
                                  Ver completo
                                </button>
                              </div>
                              <div className="log-inline-detail-grid">
                                <div className="log-inline-detail-col">
                                  <h5 className="log-inline-detail-subtitle log-inline-detail-subtitle--old">Datos Anteriores</h5>
                                  {oldFields ? oldFields.map((f) => (
                                    <div key={f.key} className="log-inline-kv">
                                      <span className="log-inline-kv-key">{f.label}:</span>
                                      <span className="log-inline-kv-val">{f.value}</span>
                                    </div>
                                  )) : <span className="log-inline-empty">—</span>}
                                </div>
                                <div className="log-inline-detail-col">
                                  <h5 className="log-inline-detail-subtitle log-inline-detail-subtitle--new">Datos Nuevos</h5>
                                  {newFields ? newFields.map((f) => {
                                    const changed = oldFields && oldFields.find((o) => o.key === f.key)?.value !== f.value;
                                    return (
                                      <div key={f.key} className={`log-inline-kv${changed ? ' log-inline-kv--changed' : ''}`}>
                                        <span className="log-inline-kv-key">{f.label}:</span>
                                        <span className="log-inline-kv-val">{f.value}</span>
                                      </div>
                                    );
                                  }) : <span className="log-inline-empty">—</span>}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div className="log-pagination-wrap">
              <span className="log-page-info">
                Página {safePage} de {totalPages}
              </span>
              <div className="log-pagination">
                <button
                  className="log-page-nav"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  aria-label="Página anterior"
                >
                  <IconChevronLeft />
                </button>

                {pageList.map((item, i) =>
                  item === '...' ? (
                    <span key={`ellipsis-${i}`} className="log-page-ellipsis">…</span>
                  ) : (
                    <button
                      key={item}
                      className={`log-page-btn${safePage === item ? ' log-page-btn--active' : ''}`}
                      onClick={() => setPage(item)}
                      aria-current={safePage === item ? 'page' : undefined}
                    >
                      {item}
                    </button>
                  )
                )}

                <button
                  className="log-page-nav"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  aria-label="Página siguiente"
                >
                  <IconChevronRight />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Detail modal ── */}
      {detailRow && (
        <DetailModal
          row={detailRow}
          userMap={userMap}
          onClose={() => setDetailRow(null)}
        />
      )}
    </div>
  );
}

export default LogsViewer;
